import {
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { useCallback, useEffect, useState } from 'react';
import { uuid } from 'uuidv4';
import CustomDialog, { CustomDialogProps } from '../CustomDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContainer: {
      height: '100%',
    },
    contentContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    photoContent: { height: '100% !important' },
    downloadIcon: {
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
  }),
);

export interface ImageData {
  contentType: string;
  fileBuffer: ArrayBuffer;
}

interface ImagePreviewDialogProps extends CustomDialogProps {
  open: boolean;
  image?: ImageData;
  title: string;
  loading?: boolean;
  customOptions?: React.ReactNode;
  handleClose: () => void;
  handleConfirm?: () => void;
}

const ImagePreviewDialog: React.FC<ImagePreviewDialogProps> = ({
  open,
  image,
  loading,
  title,
  customOptions,
  handleClose,
  handleConfirm,
}) => {
  const classes = useStyles();
  const [imageBase64, setImageBase64] = useState('');

  const onClickDownload = useCallback(async () => {
    if (image) {
      const blob = new Blob([image.fileBuffer]);
      const a = document.createElement('a');
      const blobURL = URL.createObjectURL(blob);
      a.href = blobURL;
      a.download = `${uuid()}.${image.contentType}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }, [image]);

  useEffect(() => {
    if (image) {
      setImageBase64(
        btoa(
          new Uint8Array(image?.fileBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            '',
          ),
        ),
      );
    }
  }, [image]);

  return (
    <>
      <CustomDialog
        open={open}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        maxWidth="lg"
        fullWidth
        showCloseButton
        contentDividers
        customTitle={
          <Grid
            container
            justify="space-between"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item xs={12}>
              {title}
            </Grid>
            <Grid container alignItems="center" item xs={2} wrap="nowrap">
              <Tooltip arrow title="Fazer download da foto">
                <span>
                  <IconButton disabled={loading} onClick={onClickDownload}>
                    <GetAppIcon
                      className={classes.downloadIcon}
                      color={loading ? 'disabled' : 'action'}
                    />
                  </IconButton>
                </span>
              </Tooltip>

              {customOptions && customOptions}

              <Divider
                style={{ height: '40px' }}
                orientation="vertical"
                variant="middle"
              />
            </Grid>
          </Grid>
        }
        containerStyle={classes.dialogContainer}
        contentContainerStyle={classes.contentContainer}
        keepMounted={false}
        hideDefaultButtons
      >
        {loading ? (
          <CircularProgress color="primary" />
        ) : (
          <img
            className={classes.photoContent}
            src={`data:image/jpg;image/png;image/jpeg;base64,${imageBase64}`}
            alt=""
          />
        )}
      </CustomDialog>
    </>
  );
};

export default ImagePreviewDialog;
